import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { adminApi, downloadFile } from "@/lib/adminApi";
import { PageHead } from "@/studio/ui";

const shotUrl = (f) => `${API}/studio-review/${f}`;

const Shots = ({ list, mobile }) => (
  <div className={`st-shots ${mobile ? "st-shots--mobile" : ""}`}>
    {list.map(([f, cap]) => (
      <figure className="st-shot" key={f}>
        <a href={shotUrl(f)} target="_blank" rel="noreferrer"><img src={shotUrl(f)} alt={cap} loading="lazy" data-testid={`review-shot-${f}`} /></a>
        <figcaption>{cap}</figcaption>
      </figure>
    ))}
  </div>
);

const Bullets = ({ items }) => <div className="st-prose"><ul>{items.map((i) => <li key={i}>{i}</li>)}</ul></div>;

const Rows = ({ head, rows }) => (
  <table className="st-table">
    <thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
    <tbody>{rows.map((r) => (
      <tr key={r[0]} style={{ cursor: "default" }}>
        {r.map((c, i) => <td key={i} className={i === 0 ? "" : "st-small"} style={i === 0 ? { fontWeight: 600 } : null}>{c === "Pass" ? <span className="st-tag st-tag--sage">{c}</span> : c}</td>)}
      </tr>
    ))}</tbody>
  </table>
);

const DownloadButton = ({ label }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      await downloadFile("/studio/review-pack.pdf", `hausA-Studio-${label.replace(" Review", "").replace(" ", "")}-Review-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch {
      toast.error("Could not create the review pack. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return <button className="st-btn" onClick={download} disabled={busy} data-testid="review-download-pdf-button"><Download size={15} /> {busy ? "Preparing PDF…" : "Download review pack (PDF)"}</button>;
};

export default function Review() {
  const [c, setC] = useState(null);
  useEffect(() => { adminApi.get("/studio/review-content").then((r) => setC(r.data)); }, []);
  if (!c) return <p className="st-loading">Loading…</p>;

  return (
    <div className="st-page" style={{ maxWidth: 1280 }} data-testid="studio-review-page">
      <PageHead eyebrow="Staff only · Version 1" title={c.title} lead={c.lead}><DownloadButton label={c.version_label} /></PageHead>
      {c.changes && <section className="st-panel" data-testid="review-changes"><h2 className="st-h2">What Version 1 adds</h2><Bullets items={c.changes} /></section>}
      <section className="st-panel"><h2 className="st-h2">Functional now</h2><Bullets items={c.functional} /></section>
      {c.walkthrough && <section className="st-panel" data-testid="review-walkthrough"><h2 className="st-h2">Intake → completion walkthrough</h2><Bullets items={c.walkthrough} /></section>}
      {c.setup && <section className="st-panel" data-testid="review-setup"><h2 className="st-h2">External setup required</h2><Bullets items={c.setup} /></section>}
      {c.guide && <section className="st-panel" data-testid="review-guide"><h2 className="st-h2">Operating guide: your first real client</h2><Bullets items={c.guide} /></section>}
      {c.preview_link && <section className="st-panel" data-testid="review-preview-link"><h2 className="st-h2">Client preview (sample project)</h2><a href={c.preview_link} target="_blank" rel="noreferrer" style={{ textDecoration: "underline", wordBreak: "break-all" }}>{c.preview_link}</a></section>}
      <section className="st-panel"><h2 className="st-h2">Limitations</h2><Bullets items={c.limitations} /></section>
      <section className="st-panel" data-testid="review-db-summary"><h2 className="st-h2">Database</h2><Rows head={["Collection", "Key fields", "Relationships"]} rows={c.tables} /></section>
      {c.calcs && <section className="st-panel" data-testid="review-calcs"><h2 className="st-h2">Payment calculations</h2><Rows head={c.calcs_head} rows={c.calcs} /></section>}
      {c.documents && (
        <section className="st-panel" data-testid="review-documents"><h2 className="st-h2">Sample documents</h2>
          <div className="st-btnrow">{c.documents.map(([f, cap]) => <button key={f} className="st-btn st-btn--ghost st-btn--sm" onClick={() => downloadFile(`/studio/review-doc/${f}`, f).catch(() => toast.error("Download failed"))} data-testid={`review-doc-${f}`}><Download size={13} /> {cap}</button>)}</div>
        </section>
      )}
      <section className="st-panel" data-testid="review-test-report">
        <h2 className="st-h2">Testing report</h2>
        <p className="st-muted st-small" style={{ marginTop: -8, marginBottom: 16 }}>{c.test_note}</p>
        <Rows head={["Workflow", "Result"]} rows={c.tests} />
      </section>
      <section className="st-panel"><h2 className="st-h2">Screenshots</h2><Shots list={c.screenshots} /></section>
      <section className="st-panel"><h2 className="st-h2">Mobile</h2><Shots list={c.mobile_screenshots} mobile /></section>
    </div>
  );
}
